import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../../services/apiService";

interface ApiResponse {
  message?: string;
  count?: number;
}

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [resetting, setResetting] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [settingProfile, setSettingProfile] = useState(false);
  const [deletingEnquiries, setDeletingEnquiries] = useState(false);
  const [deletingDocuments, setDeletingDocuments] = useState(false);
  const [deletingUnverified, setDeletingUnverified] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showUnassignConfirm, setShowUnassignConfirm] = useState(false);
  const [showProfileConfirm, setShowProfileConfirm] = useState(false);
  const [showDeleteEnquiriesConfirm, setShowDeleteEnquiriesConfirm] = useState(false);
  const [showDeleteDocumentsConfirm, setShowDeleteDocumentsConfirm] = useState(false);
  const [showDeleteUnverifiedConfirm, setShowDeleteUnverifiedConfirm] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const handleResetAllStatus = async () => {
    setResetting(true);
    setMessage(null);
    try {
      const response = await apiService.post<ApiResponse>('/admin/reset-all-users-status');
      setMessage({ type: 'success', text: response.data.message || 'All users status reset to Pending successfully' });
      setShowResetConfirm(false);
      
      // Navigate to user accounts page after 2 seconds
      setTimeout(() => {
        navigate('/admin/users');
      }, 2000);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to reset users status' });
    } finally {
      setResetting(false);
    }
  };

  const handleUnassignAllOperators = async () => {
    setUnassigning(true);
    setMessage(null);
    try {
      const response = await apiService.post<ApiResponse>('/admin/unassign-all-operators');
      setMessage({ type: 'success', text: response.data.message || 'All operators unassigned successfully' });
      setShowUnassignConfirm(false);
      
      // Navigate to user accounts page after 2 seconds
      setTimeout(() => {
        navigate('/admin/users');
      }, 2000);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to unassign operators' });
    } finally {
      setUnassigning(false);
    }
  };

  const handleSetProfileStatus = async () => {
    setSettingProfile(true);
    setMessage(null);
    try {
      const response = await apiService.post<ApiResponse>('/admin/set-profile-status');
      setMessage({ type: 'success', text: response.data.message || 'Profile status updated successfully' });
      setShowProfileConfirm(false);
      
      // Navigate to user accounts page after 2 seconds
      setTimeout(() => {
        navigate('/admin/users');
      }, 2000);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to set profile status' });
    } finally {
      setSettingProfile(false);
    }
  };

  const handleDeleteAllEnquiries = async () => {
    setDeletingEnquiries(true);
    setMessage(null);
    try {
      const response = await apiService.delete('/admin/delete-all-enquiries');
      setMessage({ type: 'success', text: response.data.message || 'All enquiries deleted successfully' });
      setShowDeleteEnquiriesConfirm(false);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to delete enquiries' });
    } finally {
      setDeletingEnquiries(false);
    }
  };

  const handleDeleteAllDocuments = async () => {
    setDeletingDocuments(true);
    setMessage(null);
    try {
      const response = await apiService.delete('/admin/delete-all-documents');
      setMessage({ type: 'success', text: response.data.message || 'All documents deleted successfully' });
      setShowDeleteDocumentsConfirm(false);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to delete documents' });
    } finally {
      setDeletingDocuments(false);
    }
  };

  const handleDeleteUnverifiedUsers = async () => {
    setDeletingUnverified(true);
    setMessage(null);
    try {
      const response = await apiService.post<ApiResponse>('/admin/delete-unverified-users');
      setMessage({ type: 'success', text: response.data.message || 'Unverified users deleted successfully' });
      setShowDeleteUnverifiedConfirm(false);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to delete unverified users' });
    } finally {
      setDeletingUnverified(false);
    }
  };

  return (
    <div className="container mx-auto p-6 mt-6">
      <div className="mb-6 ">
        <h1 className="text-3xl font-bold mb-2 pt-8">Settings</h1>
        <p className="text-gray-600">
          Manage bulk operations for users and operators.
        </p>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          <div className="flex items-center">
            <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'} mr-2`}></i>
            <span>{message.text}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Reset All Users Status */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-start mb-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-red-100 text-red-600">
                <i className="fas fa-undo text-2xl"></i>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Reset All Users Status</h3>
              <p className="mt-2 text-sm text-gray-600">
                Set all user status to "Pending".
              </p>
            </div>
          </div>
          <div className="mt-6">
            <button
              onClick={() => setShowResetConfirm(true)}
              disabled={resetting}
              className="w-full bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {resetting ? (
                <>
                  <svg className="inline animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Resetting...
                </>
              ) : (
                <>
                  <i className="fas fa-undo mr-2"></i>
                  Reset All Users Status
                </>
              )}
            </button>
          </div>
        </div>

        {/* Unassign All Operators */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-start mb-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-orange-100 text-orange-600">
                <i className="fas fa-user-times text-2xl"></i>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Unassign All Operators</h3>
              <p className="mt-2 text-sm text-gray-600">
                Remove all operator assignments from users.
              </p>
            </div>
          </div>
          <div className="mt-6">
            <button
              onClick={() => setShowUnassignConfirm(true)}
              disabled={unassigning}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {unassigning ? (
                <>
                  <svg className="inline animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Unassigning...
                </>
              ) : (
                <>
                  <i className="fas fa-user-times mr-2"></i>
                  Unassign All Operators
                </>
              )}
            </button>
          </div>
        </div>

        {/* Set Profile Status */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-start mb-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-green-100 text-green-600">
                <i className="fas fa-user-check text-2xl"></i>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Set Profile Status</h3>
              <p className="mt-2 text-sm text-gray-600">
                Set profile_status to 1 for users who have profiles in htax_profiles table.
              </p>
            </div>
          </div>
          <div className="mt-6">
            <button
              onClick={() => setShowProfileConfirm(true)}
              disabled={settingProfile}
              className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {settingProfile ? (
                <>
                  <svg className="inline animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Setting...
                </>
              ) : (
                <>
                  <i className="fas fa-user-check mr-2"></i>
                  Set Profile Status
                </>
              )}
            </button>
          </div>
        </div>

        {/* Delete All Enquiries */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-start mb-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-purple-100 text-purple-600">
                <i className="fas fa-trash-alt text-2xl"></i>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Delete All Enquiries</h3>
              <p className="mt-2 text-sm text-gray-600">
                Permanently delete all contact-us enquiries from the database.
              </p>
            </div>
          </div>
          <div className="mt-6">
            <button
              onClick={() => setShowDeleteEnquiriesConfirm(true)}
              disabled={deletingEnquiries}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {deletingEnquiries ? (
                <>
                  <svg className="inline animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Deleting...
                </>
              ) : (
                <>
                  <i className="fas fa-trash-alt mr-2"></i>
                  Delete All Enquiries
                </>
              )}
            </button>
          </div>
        </div>

        {/* Delete All Documents */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-start mb-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-pink-100 text-pink-600">
                <i className="fas fa-file-excel text-2xl"></i>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Delete All Documents</h3>
              <p className="mt-2 text-sm text-gray-600">
                Permanently delete all documents from htax_tax_documents table.
              </p>
            </div>
          </div>
          <div className="mt-6">
            <button
              onClick={() => setShowDeleteDocumentsConfirm(true)}
              disabled={deletingDocuments}
              className="w-full bg-pink-600 hover:bg-pink-700 text-white px-6 py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {deletingDocuments ? (
                <>
                  <svg className="inline animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Deleting...
                </>
              ) : (
                <>
                  <i className="fas fa-file-excel mr-2"></i>
                  Delete All Documents
                </>
              )}
            </button>
          </div>
        </div>

        {/* Delete Unverified Users */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-start mb-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-100 text-blue-600">
                <i className="fas fa-user-slash text-2xl"></i>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Delete Unverified Users</h3>
              <p className="mt-2 text-sm text-gray-600">
                Delete users who haven't verified their email within 24 hours.
              </p>
            </div>
          </div>
          <div className="mt-6">
            <button
              onClick={() => setShowDeleteUnverifiedConfirm(true)}
              disabled={deletingUnverified}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {deletingUnverified ? (
                <>
                  <svg className="inline animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Deleting...
                </>
              ) : (
                <>
                  <i className="fas fa-user-slash mr-2"></i>
                  Delete Unverified Users
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Reset Status Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <i className="fas fa-exclamation-triangle text-red-600 text-xl"></i>
                </div>
                <h3 className="ml-4 text-lg font-semibold text-gray-900">Confirm Reset</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to reset all user status to "Pending"? This action cannot be undone and no email notifications will be sent.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowResetConfirm(false)}
                  disabled={resetting}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetAllStatus}
                  disabled={resetting}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50"
                >
                  {resetting ? 'Resetting...' : 'Confirm Reset'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unassign Operators Confirmation Modal */}
      {showUnassignConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100">
                  <i className="fas fa-exclamation-triangle text-orange-600 text-xl"></i>
                </div>
                <h3 className="ml-4 text-lg font-semibold text-gray-900">Confirm Unassign</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to unassign all operators from users? This action cannot be undone and no email notifications will be sent.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowUnassignConfirm(false)}
                  disabled={unassigning}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUnassignAllOperators}
                  disabled={unassigning}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg disabled:opacity-50"
                >
                  {unassigning ? 'Unassigning...' : 'Confirm Unassign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Set Profile Status Confirmation Modal */}
      {showProfileConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <i className="fas fa-check-circle text-green-600 text-xl"></i>
                </div>
                <h3 className="ml-4 text-lg font-semibold text-gray-900">Confirm Profile Status Update</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to set profile_status to 1 for all users who have profiles? This will only affect users with entries in the htax_profiles table.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowProfileConfirm(false)}
                  disabled={settingProfile}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSetProfileStatus}
                  disabled={settingProfile}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
                >
                  {settingProfile ? 'Setting...' : 'Confirm Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Enquiries Confirmation Modal */}
      {showDeleteEnquiriesConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-purple-100">
                  <i className="fas fa-exclamation-triangle text-purple-600 text-xl"></i>
                </div>
                <h3 className="ml-4 text-lg font-semibold text-gray-900">Confirm Delete Enquiries</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete ALL contact-us enquiries? This action cannot be undone and will permanently remove all enquiries from the database.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteEnquiriesConfirm(false)}
                  disabled={deletingEnquiries}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAllEnquiries}
                  disabled={deletingEnquiries}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50"
                >
                  {deletingEnquiries ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Documents Confirmation Modal */}
      {showDeleteDocumentsConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-pink-100">
                  <i className="fas fa-exclamation-triangle text-pink-600 text-xl"></i>
                </div>
                <h3 className="ml-4 text-lg font-semibold text-gray-900">Confirm Delete All Documents</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                ⚠️ <strong>WARNING:</strong> This will permanently delete ALL records from the htax_tax_documents table. This action cannot be undone.
                <br /><br />
                This only deletes database records. S3 files must be deleted separately using the Archive Documents page.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteDocumentsConfirm(false)}
                  disabled={deletingDocuments}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAllDocuments}
                  disabled={deletingDocuments}
                  className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg disabled:opacity-50"
                >
                  {deletingDocuments ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Unverified Users Confirmation Modal */}
      {showDeleteUnverifiedConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                  <i className="fas fa-exclamation-triangle text-blue-600 text-xl"></i>
                </div>
                <h3 className="ml-4 text-lg font-semibold text-gray-900">Confirm Delete Unverified Users</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete all users who haven't verified their email within 24 hours? 
                <br /><br />
                This will permanently remove unverified users from the database. This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteUnverifiedConfirm(false)}
                  disabled={deletingUnverified}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUnverifiedUsers}
                  disabled={deletingUnverified}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
                >
                  {deletingUnverified ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
